import type React from "react";
import { useEffect, useRef, useState } from "react";
import { fmt } from "../../lib/sheet/utils";
import type { RawRow } from "../../lib/sheet/utils";

interface PeriodFilterProps {
  allPeriods: string[];
  selectedPeriods: Set<string>;
  onChange: (ps: Set<string>) => void;
  rows: RawRow[];
}

export const PeriodFilter: React.FC<PeriodFilterProps> = ({
  allPeriods,
  selectedPeriods,
  onChange,
  rows,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  const togglePeriod = (p: string) => {
    const next = new Set(selectedPeriods);
    next.has(p) ? next.delete(p) : next.add(p);
    onChange(next);
  };

  const selectAll = () => onChange(new Set(allPeriods));
  const selectNone = () => onChange(new Set());

  // Compute ventas per period for display in filter
  const ventasByP: Record<string, number> = {};
  for (const r of rows.filter((row) => row.rubro === "Venta")) {
    ventasByP[r.periodo] = (ventasByP[r.periodo] || 0) + r.importe;
  }

  const filtered = allPeriods.filter((p) => p.includes(filterText));
  const n = selectedPeriods.size;

  const btnLabel =
    n === allPeriods.length
      ? "Todos los períodos"
      : n === 0
        ? "Sin períodos"
        : `${n} período${n > 1 ? "s" : ""} seleccionado${n > 1 ? "s" : ""}`;

  return (
    <div className="period-dropdown" ref={panelRef} style={{ position: "relative" }}>
      <button type="button" className="period-btn" onClick={() => setIsOpen(!isOpen)}>
        <span className="period-count">{n}</span>
        <span id="periodBtnText">{btnLabel}</span>
        <span className="period-arrow">▾</span>
      </button>

      {isOpen && (
        <div className="period-panel open">
          <div className="period-header">
            <input
              type="text"
              className="period-search"
              placeholder="Filtrar periodos..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            <div className="period-actions">
              <button type="button" onClick={selectAll}>
                Todos
              </button>
              <button type="button" onClick={selectNone}>
                Ninguno
              </button>
            </div>
          </div>
          <div className="period-list">
            {filtered.map((p) => (
              <label key={p} className="period-item">
                <input
                  type="checkbox"
                  checked={selectedPeriods.has(p)}
                  onChange={() => togglePeriod(p)}
                />
                <span className="period-item-name">{p}</span>
                <span className="pval">{fmt(ventasByP[p] || 0)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .period-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          background: var(--color-surface-2);
          border: 1px solid var(--color-border);
          color: var(--color-text);
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          min-width: 220px;
        }
        .period-count {
          background: var(--color-accent);
          color: white;
          border-radius: 4px;
          padding: 1px 6px;
          font-size: 10px;
          font-weight: 700;
        }
        .period-panel {
          position: absolute;
          top: calc(100% + 5px);
          left: 0;
          background: var(--color-surface-2);
          border: 1px solid var(--color-border);
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.4);
          z-index: 1000;
          width: 280px;
          display: flex;
          flex-direction: column;
        }
        .period-header {
          padding: 12px;
          border-bottom: 1px solid var(--color-border);
        }
        .period-search {
          width: 100%;
          background: var(--color-bg);
          border: 1px solid var(--color-border);
          color: var(--color-text);
          padding: 6px 10px;
          border-radius: 4px;
          font-size: 12px;
          margin-bottom: 10px;
          box-sizing: border-box;
        }
        .period-actions {
          display: flex;
          gap: 8px;
        }
        .period-actions button {
          flex: 1;
          background: transparent;
          border: 1px solid var(--color-border);
          color: var(--color-muted);
          font-size: 10px;
          padding: 4px;
          cursor: pointer;
          border-radius: 4px;
        }
        .period-actions button:hover { color: var(--color-text); border-color: var(--color-muted); }
        .period-list {
          max-height: 300px;
          overflow-y: auto;
          padding: 4px 0;
        }
        .period-item {
          display: flex;
          align-items: center;
          padding: 6px 12px;
          cursor: pointer;
          font-size: 12px;
          gap: 10px;
        }
        .period-item:hover { background: rgba(255,255,255,0.03); }
        .period-item-name { flex: 1; }
        .pval { font-family: var(--font-mono); color: var(--color-muted); font-size: 11px; }
      `}</style>
    </div>
  );
};
